/**
 * Staff who never got in (#120, 2026-10-14). The hotel added 24 staff and none of them ever logged in:
 * nobody was invited, and the manager typed every clocked shift by hand. Ansatte shows per person
 * whether they are invited, logged in and on the phone, sends the welcome to everyone not yet invited,
 * and shows one person's welcome link as a QR code for somebody with no address. «Appen på papir» is
 * a sheet with the store and web addresses as QR codes to print or show.
 */
import { post } from '@/api/client';
import type { ApiUser } from '@/api/people';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Button, Drawer, toast } from '@/ui';
import { fmtDs } from '@/ui/format';
import { Status } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import qrcode from 'qrcode-generator';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** the phone app in the stores, as backend/services/appVersion.js STORE_URLS names them */
export const STORE_URLS = {
  ios: 'https://apps.apple.com/app/id6670785637',
  android: 'https://play.google.com/store/apps/details?id=com.timefly.scheduler',
};

const DAY_MS = 86_400_000;
type Who = Pick<ApiUser, 'id' | 'email' | 'active_status' | 'last_login_at' | 'invited_at'>;

/** Logged in, invited (the newest welcome, mailed or shown), or neither. */
export function invitation(u: Who): { kind: 'in' | 'invited' | 'none'; date: string | null } {
  if (u.last_login_at) return { kind: 'in', date: oslo(u.last_login_at)?.date ?? null };
  if (u.invited_at) return { kind: 'invited', date: oslo(u.invited_at)?.date ?? null };
  return { kind: 'none', date: null };
}

/**
 * Whom «Send til alle» mails, the server's rule (POST /admin/invitations): active, never logged in, an
 * address, not the person pressing it; never invited, or with `again` invited more than a day ago.
 */
export function bulkTargets(people: Who[], meId: number | undefined, again: boolean, now = Date.now()) {
  return people.filter(
    (u) =>
      u.active_status &&
      !u.last_login_at &&
      !!u.email &&
      u.id !== meId &&
      (again ? !!u.invited_at && now - new Date(u.invited_at).getTime() > DAY_MS : !u.invited_at),
  );
}

/** The chip in the Innlogging column, and whether the phone app is registered. */
export function InviteState({ u }: { u: Who & Pick<ApiUser, 'has_app'> }) {
  const s = invitation(u);
  return (
    <>
      {s.kind === 'in' ? (
        <Status tone="ok">{T('Logget inn {d}', { d: s.date ? fmtDs(s.date) : '' })}</Status>
      ) : s.kind === 'invited' ? (
        <Status tone="act">{T('Invitert {d}', { d: s.date ? fmtDs(s.date) : '' })}</Status>
      ) : (
        <Status tone="warn">{T('Ikke invitert')}</Status>
      )}
      {u.has_app ? (
        <small>{T('Bruker appen')}</small>
      ) : (
        s.kind === 'in' && <small>{T('Ikke appen ennå')}</small>
      )}
    </>
  );
}

/** A QR code as an SVG of dark squares, error correction M: readable from a phone held to a screen or paper. */
export function Qr({ text, label, size = 168 }: { text: string; label: string; size?: number }) {
  const q = qrcode(0, 'M');
  q.addData(text);
  q.make();
  const n = q.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r++)
    for (let c = 0; c < n; c++) if (q.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
  return (
    <svg
      viewBox={`0 0 ${n + 8} ${n + 8}`}
      width={size}
      height={size}
      role="img"
      aria-label={label}
      data-testid="qr"
      shapeRendering="crispEdges"
    >
      <rect width="100%" height="100%" fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  );
}

/** A sheet on <body> while printing only, the way ui/print.tsx prints the plan. */
function PrintOnly({ children }: { children: React.ReactNode }) {
  return createPortal(<div className="printsheet invsheet">{children}</div>, document.body);
}

/**
 * One person's welcome link, made when the drawer opens (POST /admin/users/:id/welcome-link): the QR
 * code to scan from the manager's screen, the link to copy into a message, and the username. Making it
 * ends any earlier link, mailed or shown.
 */
export function LinkDrawer({
  u,
  onClose,
}: { u: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null; onClose: () => void }) {
  const [got, setGot] = useState<{ link: string; username: string } | { error: string } | null>(null);
  const qc = useQueryClient();
  const id = u?.id;
  useEffect(() => {
    setGot(null);
    if (id == null) return;
    post<{ link: string; username: string }>(`/admin/users/${id}/welcome-link`, {})
      .then((r) => {
        setGot(r);
        void qc.invalidateQueries({ queryKey: ['users'] }); // it counts as an invitation
      })
      .catch((e: Error) => setGot({ error: e.message }));
  }, [id, qc]);
  const name = u ? [u.name, u.lastname].filter(Boolean).join(' ') : '';
  const ok = got && 'link' in got ? got : null;
  return (
    <Drawer
      open={u != null}
      onClose={onClose}
      title={T('Invitasjon til {name}', { name })}
      sub={T(
        'La personen skanne koden, eller send lenken i en melding. Den gjelder i 7 dager og kan brukes én gang.',
      )}
      footer={
        ok && (
          <>
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(ok.link).then(
                  () => toast(T('Lenken er kopiert')),
                  () => toast(T('Kunne ikke kopiere. Merk lenken og kopier den selv.')),
                );
              }}
            >
              {T('Kopier lenken')}
            </Button>
            <Button onClick={() => window.print()}>{T('Skriv ut')}</Button>
          </>
        )
      }
    >
      {got == null && <p>{T('Lager lenken …')}</p>}
      {got && 'error' in got && <div className="al dgr">{got.error}</div>}
      {ok && (
        <div className="inv-link" data-testid="invite-link">
          <Qr text={ok.link} label={T('QR-kode for invitasjonen til {name}', { name })} />
          <p>
            {T('Brukernavn')}: <b>{ok.username}</b>
          </p>
          <p className="inv-url">{ok.link}</p>
          <p className="sub">{T('En lenke sendt tidligere slutter å virke nå.')}</p>
          <PrintOnly>
            <h1>{T('Velkommen til Timefly, {name}', { name })}</h1>
            <Qr text={ok.link} label={T('QR-kode for invitasjonen til {name}', { name })} size={260} />
            <p>{T('Skann koden og velg et passord. Brukernavnet ditt er {u}.', { u: ok.username })}</p>
            <p>{T('Koden gjelder i 7 dager og kan brukes én gang.')}</p>
          </PrintOnly>
        </div>
      )}
    </Drawer>
  );
}

/** «Appen på papir»: the App Store, Google Play and the web login as QR codes, to print or show. */
export function AppSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const web = `${window.location.origin}/login`;
  const codes = [
    { k: 'ios', title: T('iPhone: App Store'), url: STORE_URLS.ios },
    { k: 'android', title: T('Android: Google Play'), url: STORE_URLS.android },
    { k: 'web', title: T('På nett'), url: web },
  ];
  const grid = (size: number) => (
    <div className="inv-codes">
      {codes.map((c) => (
        <figure key={c.k}>
          <Qr text={c.url} label={c.title} size={size} />
          <figcaption>
            <b>{c.title}</b>
            <span>{c.url}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={T('Appen på papir')}
      sub={T(
        'Heng opp arket eller vis det på skjermen. Brukernavn og passord får hver ansatt i invitasjonen.',
      )}
      footer={<Button onClick={() => window.print()}>{T('Skriv ut')}</Button>}
    >
      {open && grid(140)}
      {open && (
        <PrintOnly>
          <h1>{T('Timefly: vaktplan og timer på mobilen')}</h1>
          {grid(200)}
          <p>
            {T(
              'Logg inn med brukernavnet og passordet fra invitasjonen. Spør lederen om du ikke har fått den.',
            )}
          </p>
        </PrintOnly>
      )}
    </Drawer>
  );
}
